import React, { useEffect, useMemo, useState } from 'react';
import { setPageMeta } from '../lib/seo';

const docsModel = {
  onboarding: {
    title: 'Getting Started',
    topics: [
      {
        key: 'overview',
        title: 'Platform overview',
        body: [
          { type: 'text', value: 'CLOUNIQ provides time-based GPU workspaces and VPS instances that can be activated in minutes.' },
          { type: 'list', value: ['Create account and verify email', 'Choose GPU or VPS profile', 'Pay for session window and receive access details'] },
        ],
      },
      {
        key: 'purchase-flow',
        title: 'Purchase and activation flow',
        body: [
          { type: 'text', value: 'After payment, credentials are delivered close to your selected start time.' },
          { type: 'list', value: ['Cloud PC: connect with RustDesk ID and password', 'Cloud VPS: connect with SSH or RDP credentials', 'Session ends automatically at the booked time'] },
        ],
      },
      {
        key: 'performance',
        title: 'Performance checklist',
        body: [
          { type: 'text', value: 'For a stable remote experience use a reliable internet line and avoid heavy local downloads during sessions.' },
          { type: 'list', value: ['Recommended: 50 Mbps+', 'Minimum: 20 Mbps for Cloud PC, 10 Mbps for VPS', 'Use wired connection when possible'] },
        ],
      },
    ],
  },
  cloudpc: {
    title: 'Cloud PC Access',
    topics: [
      {
        key: 'rustdesk-install',
        title: 'Install RustDesk',
        body: [
          { type: 'text', value: 'Download RustDesk from the official site and install it with default settings.' },
          { type: 'link', value: { text: 'Official RustDesk website', url: 'https://rustdesk.com' } },
        ],
      },
      {
        key: 'rustdesk-connect',
        title: 'Connect to your Cloud PC',
        body: [
          { type: 'text', value: 'Open RustDesk, paste the provided remote ID, and enter your password when prompted.' },
          { type: 'list', ordered: true, value: ['Open RustDesk', 'Paste Remote ID', 'Click connect', 'Enter password', 'Start working in your cloud desktop'] },
        ],
      },
      {
        key: 'rustdesk-security',
        title: 'Security best practices',
        body: [
          { type: 'list', value: ['Never share remote credentials', 'Rotate password if access seems suspicious', 'Always disconnect after session'] },
        ],
      },
    ],
  },
  vps: {
    title: 'Cloud VPS Access',
    topics: [
      {
        key: 'vps-linux',
        title: 'Linux VPS over SSH',
        body: [
          { type: 'text', value: 'Use your provided username and IP to connect from terminal:' },
          { type: 'code', value: 'ssh <username>@<ip_address>' },
        ],
      },
      {
        key: 'vps-windows',
        title: 'Windows VPS over RDP',
        body: [
          { type: 'list', ordered: true, value: ['Open Remote Desktop client', 'Enter IP address', 'Authenticate with provided username and password'] },
        ],
      },
      {
        key: 'vps-hardening',
        title: 'Recommended hardening',
        body: [
          { type: 'list', value: ['Update system packages early', 'Restrict unused ports', 'Use strong non-default credentials', 'Store access details securely'] },
        ],
      },
    ],
  },
  support: {
    title: 'Support',
    topics: [
      {
        key: 'help',
        title: 'Contact channels',
        body: [
          { type: 'text', value: 'For billing, access, or technical support contact the team directly.' },
          { type: 'link', value: { text: 'info@clouniq.com', url: 'mailto:info@clouniq.com' } },
        ],
      },
      {
        key: 'incident',
        title: 'What to include in incident reports',
        body: [
          { type: 'list', value: ['Order or session ID', 'Approximate issue time', 'Error screenshot or message', 'Connection method (RustDesk/SSH/RDP)'] },
        ],
      },
    ],
  },
};

function Docs() {
  const sectionKeys = Object.keys(docsModel);
  const [activeSection, setActiveSection] = useState(sectionKeys[0]);
  const [activeTopic, setActiveTopic] = useState(docsModel[sectionKeys[0]].topics[0].key);

  useEffect(() => {
    setPageMeta({
      title: 'Documentation | Clouniq',
      description: 'Read setup guides, security tips, and support workflows for Cloud PC and Cloud VPS.',
    });
  }, []);

  const flattened = useMemo(() => (
    sectionKeys.flatMap((sectionKey) => docsModel[sectionKey].topics.map((topic) => ({ sectionKey, topicKey: topic.key })))
  ), [sectionKeys]);

  const currentIndex = flattened.findIndex((item) => item.topicKey === activeTopic);
  const previous = currentIndex > 0 ? flattened[currentIndex - 1] : null;
  const next = currentIndex < flattened.length - 1 ? flattened[currentIndex + 1] : null;

  const section = docsModel[activeSection];
  const topic = section.topics.find((entry) => entry.key === activeTopic) || section.topics[0];

  const goToTopic = (entry) => {
    if (!entry) return;
    setActiveSection(entry.sectionKey);
    setActiveTopic(entry.topicKey);
  };

  const renderBlock = (block, index) => {
    if (block.type === 'text') {
      return <p className="docs-text" key={`txt-${index}`}>{block.value}</p>;
    }

    if (block.type === 'list') {
      const ListTag = block.ordered ? 'ol' : 'ul';
      return (
        <ListTag className="docs-list" key={`lst-${index}`}>
          {(block.value || []).map((item) => <li key={item}>{item}</li>)}
        </ListTag>
      );
    }

    if (block.type === 'link') {
      return (
        <a
          key={`lnk-${index}`}
          href={block.value.url}
          className="docs-link docs-link-block"
          target="_blank"
          rel="noopener noreferrer"
        >
          {block.value.text}
        </a>
      );
    }

    if (block.type === 'code') {
      return (
        <pre className="docs-code-inline" key={`cd-${index}`}>
          <code>{block.value}</code>
        </pre>
      );
    }

    return null;
  };

  return (
    <section className="docs-section nova-docs-page docs-rebuild-page">
      <div className="container">
        <div className="docs-rebuild-hero">
          <span className="section-tag">Documentation</span>
          <h1 className="docs-title">Setup guides for Cloud PC and Cloud VPS</h1>
          <p className="docs-text">Select a section and follow short, practical steps.</p>
        </div>

        <div className="docs-rebuild-section-tabs">
          {sectionKeys.map((key) => (
            <button
              key={key}
              type="button"
              className={`docs-rebuild-section-tab ${activeSection === key ? 'active' : ''}`}
              onClick={() => {
                setActiveSection(key);
                setActiveTopic(docsModel[key].topics[0].key);
              }}
            >
              {docsModel[key].title}
            </button>
          ))}
        </div>

        <div className="docs-rebuild-layout">
          <aside className="docs-rebuild-topic-list">
            <h3>{section.title}</h3>
            <div className="docs-rebuild-topics">
              {section.topics.map((entry) => (
                <button
                  key={entry.key}
                  type="button"
                  className={`docs-rebuild-topic-btn ${entry.key === topic.key ? 'active' : ''}`}
                  onClick={() => setActiveTopic(entry.key)}
                >
                  {entry.title}
                </button>
              ))}
            </div>
          </aside>

          <article className="docs-rebuild-article">
            <header className="docs-content-header">
              <span className="docs-breadcrumb">{section.title} / {topic.title}</span>
              <h2 className="docs-title">{topic.title}</h2>
            </header>

            <div className="docs-body">
              {topic.body.map(renderBlock)}

              <div className="docs-rebuild-nav">
                <button
                  type="button"
                  className={`docs-rebuild-nav-btn ${!previous ? 'disabled' : ''}`}
                  onClick={() => goToTopic(previous)}
                  disabled={!previous}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className={`docs-rebuild-nav-btn ${!next ? 'disabled' : ''}`}
                  onClick={() => goToTopic(next)}
                  disabled={!next}
                >
                  Next
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

export default Docs;




